/**
 * Single source for GoHighLevel marketplace OAuth config.
 *
 * - Local: .env.local (REACT_APP_GHL_CLIENT_ID, REACT_APP_GHL_REDIRECT_URI)
 * - Deployed: same vars in Amplify
 *
 * The client secret is deliberately absent. GHL's token exchange happens on the
 * backend (social-management), unlike the Facebook/GMB flows in this app which
 * still ship a client_secret to the browser.
 */
const getEnv = (key: string): string => {
  const value = process.env[key];
  return typeof value === "string" ? value : "";
};

export const getGHLClientId = (): string => getEnv("REACT_APP_GHL_CLIENT_ID");

/**
 * Marketplace app *version* id, sent as `version_id` on the consent URL.
 *
 * Required, and the reason the first working consent screen took so long to
 * reach. HighLevel's v1 consent URL resolves the app's current published
 * version implicitly; a Draft app has none, so it fails with
 * `error.noAppVersionIdFound` / "No integration found with the id: <appId>".
 * The v2 URL takes the version explicitly and works for a Draft, which is what
 * lets us test before the app is published to the marketplace.
 *
 * Defaults to the app id because that is what HighLevel's own generated install
 * link uses for this app (version_id === appId). Kept as its own env var rather
 * than derived, because the two diverge as soon as a second version exists.
 */
export const getGHLVersionId = (): string => {
  const fromEnv = getEnv("REACT_APP_GHL_VERSION_ID").trim();
  if (fromEnv) return fromEnv;
  // client_id is "{appId}-{suffix}"; the app id alone is the fallback version.
  return getGHLClientId().split("-")[0] ?? "";
};

/**
 * GHL requires the redirect_uri on the token exchange to byte-match both the
 * one used on the consent request AND one registered on the marketplace app —
 * a trailing slash or a different host fails the install. Prefer the explicit
 * env var; the origin fallback only exists so local dev works without config.
 *
 * The path is "/connect/crm/callback" and NOT "/connect/ghl/callback" on
 * purpose. HighLevel's marketplace rejects any redirect URL containing a
 * HighLevel reference for white-label apps — registering a "/ghl/" path fails
 * with "The redirect uri contains a Highlevel reference." Do not rename this
 * back. Internal identifiers (the GHL platform key, ghl_oauth.py, the
 * oauth_state provider key) are unaffected: HighLevel only ever sees the URL.
 */
export const getGHLRedirectUri = (): string => {
  const fromEnv = getEnv("REACT_APP_GHL_REDIRECT_URI").trim();
  if (fromEnv) return fromEnv;
  return `${window.location.origin}/connect/crm/callback`;
};

/**
 * Scopes requested at consent time. Must match what the marketplace app has
 * configured, and mirrors GHL_SCOPES in
 * social-management/layers/social-common/python/ghl_oauth.py.
 *
 * CORRECTION (2026-09-03): `locations/tags.write`/`readonly` DO exist as real
 * HighLevel OAuth scopes (confirmed against the marketplace app's own scope
 * picker) — an earlier version of this comment was wrong on that. Still
 * intentionally absent here: `ghl_tag_contact` (chat-service) sets tags via
 * `PUT /contacts/{contactId}`, covered by `contacts.write` below. Add the
 * locations/tags.* pair only if something starts calling the separate
 * location-level tags endpoints.
 */
export const GHL_SCOPES = [
  "contacts.readonly",
  "contacts.write",
  "conversations.readonly",
  "conversations.write",
  "conversations/message.readonly",
  "conversations/message.write",
  "calendars.readonly",
  "calendars.write",
  "calendars/events.readonly",
  "calendars/events.write",
  "calendars/groups.readonly",
  "calendars/groups.write",
  // SGL-245 (2026-09-03): the AI Receptionist's calendar tools need the
  // business's own timezone (GET /locations/{locationId} -> `timezone`), so
  // free-slots is asked for the right 24-hour window instead of a UTC one.
  // Read-only — `locations.write` would let us reconfigure a customer's
  // sub-account, which we should never do. Existing connections do NOT pick
  // this up until the customer reconnects GHL; a token minted before this
  // change keeps its old scope set.
  "locations.readonly",
  // Agency installs. Both are Sub-Account scopes despite the name, and both are
  // needed: when an agency admin (rather than a sub-account user) installs the
  // app, HighLevel returns a Company token with no locationId, and the backend
  // has to list the installed sub-accounts (oauth.readonly) and mint a location
  // token for each (oauth.write) before it can store anything.
  "oauth.readonly",
  "oauth.write",
] as const;
